import { SetStateAction, Dispatch, ChangeEvent, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubDynamicIcon,
  DevHubFormGroup,
  devhubMakeStyles,
  DevHubSwitch,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { debounce } from 'debounce';
import { matchSorter } from 'match-sorter';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import { ResponsibleParty } from '../insights/types/RoleTypes';
import {
  CorporateEntity,
  getCorporateEntityFromEmail,
} from '../onboarding/types/onboardingTypes';
import {
  checkOwnerEligibility,
  OwnerEligibilityTier,
} from '../../util/ownerValidation';
import { OwnerLevelDialog } from './OwnerLevelDialog';
import { CGAOwnerLevelDialog } from './CGAOwnerLevelDialog';

const UsersQuery = gql`
  query PartyUserSearch($filter: UserListFilter!) {
    identity {
      users(filter: $filter) {
        login
        firstName
        lastName
        displayName
        email
        title
      }
    }
  }
`;

const GroupsQuery = gql`
  query PartyUserGroups($filter: GroupListFilter!) {
    identity {
      groups(filter: $filter) {
        name
      }
    }
  }
`;

interface Props {
  entity?: CorporateEntity;
  contractors?: boolean;
  noMargin?: boolean;
  groupEnabled?: boolean;
  helperText?: string;
  className?: any;
  name?: string;
  label?: string;
  setSelectedParty: Dispatch<SetStateAction<ResponsibleParty>>;
  selectedParty?: ResponsibleParty;
  required?: boolean;
  error?: boolean;
  onBlur?: () => void;
  /** Controls dialog verbiage: 'owner' for assignment, 'report' for CGA. */
  context?: 'owner' | 'report';
}

const useStyles = devhubMakeStyles({
  search: {
    marginTop: '0.25rem',
  },
});

/**
 * Search for a party or group bases on prop isUser
 * @param props Props of the component
 * @param props.groupEnabled Set to true to allow searching of groups and enabled the toggle
 * @param props.helperText Helper text to display bellow input box
 * @param props.setSelectedParty Setter hook for value selected in form
 * @param props.name name value passed to the component
 */
export function PartySearch({
  entity = CorporateEntity.CABLE,
  contractors = true,
  groupEnabled = false,
  noMargin = false,
  helperText,
  className,
  name = '',
  label,
  setSelectedParty,
  selectedParty = {} as ResponsibleParty,
  required = true,
  error,
  onBlur,
  context = 'owner',
}: Props) {
  const [inputValue, setInputValue] = useState<string>('');
  const [isUsers, setIsUsers] = useState<boolean>(true);
  const [users, setUsers] = useState<Array<ResponsibleParty>>([]);
  const [groups, setGroups] = useState<Array<ResponsibleParty>>([]);
  const [levelCheckLoading, setLevelCheckLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogTier, setDialogTier] = useState<OwnerEligibilityTier>('allow');
  const [pendingParty, setPendingParty] = useState<ResponsibleParty>(
    {} as ResponsibleParty,
  );
  const [autocompleteKey, setAutocompleteKey] = useState<number>(0);
  const classes = useStyles();

  const [getUsers, { loading: usersLoading, error: usersError }] = useLazyQuery(
    UsersQuery,
    {
      errorPolicy: 'all',
    },
  );

  const [getGroups, { loading: groupsLoading, error: groupsError }] =
    useLazyQuery(GroupsQuery, {
      errorPolicy: 'all',
    });

  function filterUser(user: ResponsibleParty) {
    const login = user?.login?.toLowerCase();
    return (
      !login?.startsWith('adm_') &&
      !login?.startsWith('!') &&
      !login?.startsWith('svc') &&
      user?.firstName !== null &&
      user?.lastName !== null
    );
  }

  function filterBusinessPartners(user: ResponsibleParty) {
    const login = user?.login?.toLowerCase();
    if (!contractors) return !login?.startsWith('bp-');
    return true;
  }

  // @ts-ignore -- temporarily disable for reverting corp entity domain checks
  function filterUserOnEntity(user: ResponsibleParty) {
    if (!user?.email) {
      return false;
    }
    const corpEntity = getCorporateEntityFromEmail(user?.email);
    return entity === corpEntity;
  }

  const handleSearch = debounce((value: string) => {
    if (value && isUsers) {
      getUsers({
        variables: contractors
          ? { filter: { name: value } }
          : { filter: { name: value, contractor: contractors } },
      }).then(userResults => {
        if (userResults?.data?.identity?.users?.length > 0) {
          const filteredUsers = userResults.data.identity.users
            .filter(filterUser)
            .filter(filterBusinessPartners);
          // Temporarily disable for reverting corp entity domain checks
          // .filter(filterUserOnEntity);

          setUsers(filteredUsers);
        }
      });
    } else if (value && !isUsers) {
      getGroups({
        variables: { filter: { name: value } },
      }).then(groupResults => {
        if (groupResults?.data?.identity?.groups?.length > 0) {
          setGroups(groupResults.data.identity.groups);
        }
      });
    }
  }, 500);

  // Event Handler to set selected party from a drop down
  const onSelect = async (
    _: Object,
    value: ResponsibleParty | null,
    reason: DevHubAutocompleteChangeReason,
  ) => {
    if (reason === 'clear' && !value) {
      setSelectedParty({} as ResponsibleParty);
    }
    if (!value) {
      return;
    }

    // Employee level validation — fires for all user selections when enabled.
    // Non-Cable users will simply not be found in the hierarchy table
    // and the check will fail-open (proceed without dialog).
    const empLevelEnabled = ConfigHelper.instance.isFeatureEnabled(
      Feature.EMP_LEVEL_CHECK,
    );
    if (isUsers && empLevelEnabled && value.login) {
      setLevelCheckLoading(true);
      const result = await checkOwnerEligibility(value.login);
      setLevelCheckLoading(false);

      if (result.tier === 'exclude' || result.tier === 'warn') {
        setPendingParty(value);
        setDialogTier(result.tier);
        setDialogOpen(true);
        return;
      }
    }

    setSelectedParty(value);
  };

  /** Dialog dismissed — reset selection and return to search. */
  const handleDialogClose = () => {
    setDialogOpen(false);
    setPendingParty({} as ResponsibleParty);
    setSelectedParty({} as ResponsibleParty);
    setAutocompleteKey(prev => prev + 1);
  };

  /** Warn tier: user chose "Assign Anyway". */
  const handleDialogConfirm = () => {
    setDialogOpen(false);
    setSelectedParty(pendingParty);
    setPendingParty({} as ResponsibleParty);
  };

  // Event Handler for the text box and to trigger the query after 3 keystrokes
  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
    if (value.length >= 3) {
      handleSearch(value);
    }
  };

  // Toggle switch Event Handler
  const handleToggle = () => {
    setUsers([]);
    setGroups([]);
    setSelectedParty({} as ResponsibleParty);
    setIsUsers(prevState => !prevState);
  };

  // Returns "Firstname Lastname (login)" option label
  const getOptionLabel = (option: ResponsibleParty) => {
    if (isUsers) {
      return `${option?.firstName} ${
        option?.lastName
      } (${option?.login?.toLowerCase()})`;
    }
    return option?.name ?? '';
  };

  const partyType = () => {
    return isUsers ? 'User' : 'Group';
  };

  // Fuzzy searching for usernames
  const filterOptions = () => {
    if (isUsers) {
      return matchSorter(users, inputValue, {
        // Sets to not filter out results just to rank them
        threshold: matchSorter.rankings.NO_MATCH,
        keys: ['firstName', 'lastName', 'login'],
      });
    }
    return matchSorter(groups, inputValue, {
      keys: ['name'],
    });
  };

  return (
    <>
      {usersError || groupsError ? (
        <DevHubTextField
          error
          label="Error"
          className={className}
          defaultValue={`No ${partyType()} Available`}
          helperText={`Failed to fetch ${partyType()} details.`}
          variant="outlined"
        />
      ) : (
        <>
          {groupEnabled && (
            <DevHubFormGroup row>
              <DevHubTypography>Groups</DevHubTypography>
              <DevHubSwitch
                checked={isUsers}
                onChange={handleToggle}
                name="users"
              />
              <DevHubTypography>Users</DevHubTypography>
            </DevHubFormGroup>
          )}
          <DevHubAutocomplete
            // className={className}
            key={`${isUsers ? 'users' : 'groups'}-${autocompleteKey}`}
            fullWidth
            disabled={levelCheckLoading}
            loading={usersLoading || groupsLoading || levelCheckLoading}
            loadingText={levelCheckLoading ? 'Checking...' : 'Loading..'}
            options={isUsers ? users : groups}
            filterOptions={filterOptions}
            onChange={onSelect}
            onBlur={onBlur}
            defaultValue={selectedParty?.login ? selectedParty : undefined}
            getOptionLabel={option => getOptionLabel(option)}
            forcePopupIcon
            renderInput={params => (
              <DevHubTextField
                {...params}
                label={label ?? `Search for a ${partyType()}`}
                InputLabelProps={
                  required ? { required: true } : { required: false }
                }
                margin="normal"
                noMargin={noMargin}
                name={name}
                helperText={helperText}
                error={error}
                variant="outlined"
                required
                onChange={onChange}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {params.InputProps.endAdornment}
                      {label && (
                        <span className={classes.search}>
                          <DevHubDynamicIcon iconName="Search" />
                        </span>
                      )}
                    </>
                  ),
                }}
              />
            )}
          />
        </>
      )}
      {context === 'report' ? (
        <CGAOwnerLevelDialog
          open={dialogOpen}
          userName={`${pendingParty.firstName ?? ''} ${pendingParty.lastName ?? ''}`.trim()}
          userTitle={pendingParty.title ?? ''}
          tier={dialogTier}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
        />
      ) : (
        <OwnerLevelDialog
          open={dialogOpen}
          userName={`${pendingParty.firstName ?? ''} ${pendingParty.lastName ?? ''}`.trim()}
          userTitle={pendingParty.title ?? ''}
          tier={dialogTier}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
        />
      )}
    </>
  );
}
